import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import HelpInfo from '@/components/HelpInfo';
import AgentOverviewDashboard from '@/components/AgentOverviewDashboard';
import MarketingDashboardV2 from '@/pages/MarketingDashboardV2';
import LeadershipOverviewBlock from '@/components/LeadershipOverviewBlock';
import RevenueWidget from '@/components/RevenueWidget';
import CEOExecutiveSummary from '@/components/CEOExecutiveSummary';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { LayoutDashboard, Megaphone as MegaphoneIcon, Settings2 } from 'lucide-react';

// Sales / marketing / CR agents get the agent-perspective dashboard
// instead of the company-wide contact analytics below.
const AGENT_ROLES = [
  'sales_agent', 'marketing_agent', 'cr_agent', 'customer_relations_agent',
  'sales_rep', 'marketing_rep', 'agent',
];
import { 
  TrendingUp, Users, BarChart3, PieChart as PieChartIcon, 
  UserCircle, Globe, Calendar, RefreshCw,
  ArrowUpRight, ArrowDownRight, Target, Zap, Phone, PhoneOff,
  Mic, Video, Play
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color palette for charts - Desert Modernism theme
const COLORS = ['#F97316', '#0EA5E9', '#10B981', '#8B5CF6', '#EC4899', '#F59E0B', '#06B6D4', '#84CC16'];
const STATUS_COLORS = {
  'Lead': '#10B981',
  'First Attempt - No Response': '#F59E0B',
  'Second Attempt - No Response': '#F97316',
  'Third Attempt - No Response': '#EF4444',
  'Broker Contacted': '#14B8A6',
  'Connected & Interested': '#22C55E',
  'Information Shared': '#0EA5E9',
  'Collecting Requirements': '#6366F1',
  'Not Interested': '#EF4444',
  'No Status': '#94A3B8'
};

// ---- Section banner ----------------------------------------------------
// Lightweight labelled divider rendered above each operational-view
// chunk so a CEO toggling the department selector immediately sees
// *which* set of widgets was just revealed. Keeps the page legible
// when several blocks are visible at once (e.g. "All Departments").
const SECTION_TONE = {
  slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   fg: 'text-slate-700',   icon: 'text-slate-500'   },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    fg: 'text-blue-700',    icon: 'text-blue-500'    },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  fg: 'text-violet-700',  icon: 'text-violet-500'  },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   fg: 'text-amber-700',   icon: 'text-amber-500'   },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', fg: 'text-emerald-700', icon: 'text-emerald-500' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    fg: 'text-rose-700',    icon: 'text-rose-500'    },
};

const SectionBanner = ({ tone = 'slate', icon: Icon, title, subtitle }) => {
  const t = SECTION_TONE[tone] || SECTION_TONE.slate;
  return (
    <div
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${t.bg} ${t.border}`}
      data-testid={`section-banner-${tone}`}
    >
      {Icon && (
        <div className={`w-8 h-8 rounded-lg bg-white border ${t.border} flex items-center justify-center ${t.icon}`}>
          <Icon size={14} />
        </div>
      )}
      <div className="min-w-0">
        <p className={`text-sm font-semibold leading-tight ${t.fg}`}>{title}</p>
        {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { token, user, loading: authLoading } = useAuth();
  const isAgent = AGENT_ROLES.includes((user?.role || '').toLowerCase());
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState(null);
  const [leadStatusBreakdown, setLeadStatusBreakdown] = useState([]);
  const [lifecycleBreakdown, setLifecycleBreakdown] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);
  const [ownerBreakdown, setOwnerBreakdown] = useState([]);
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [marketingBreakdown, setMarketingBreakdown] = useState([]);
  const [contactInfoBreakdown, setContactInfoBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [startingQuickMeeting, setStartingQuickMeeting] = useState(false);

  // ---- Department-view filter ------------------------------------------
  // Controls which OPERATIONAL widgets appear on the page. Strategic
  // content (Executive Summary, Revenue Performance, Leadership
  // Overview) is always shown — these are the CEO's headline view.
  // The operational sections (Quick Start Meeting card, contact-stats
  // rows, the four distribution charts, Activity Timeline, Contact-Info
  // charts, the inner Agent Talktime panel) are department-scoped and
  // hide by default so a CEO landing here sees the strategic
  // scorecard without the operational noise.
  //
  //   strategic   — only the CEO essentials (default)
  //   sales       — adds Sales operational widgets (lead status,
  //                 lifecycle, contacts-by-owner, agent talktime)
  //   marketing   — adds Marketing operational widgets (lead source
  //                 distribution, activity timeline, marketing splits)
  //   operations  — adds Admin tools (Quick Start Meeting, contact
  //                 stat rows, contact-info charts, Sync HubSpot)
  //   all         — everything visible
  const [viewMode, setViewMode] = useState('strategic');
  // Helper: returns true when the current viewMode matches any of
  // the buckets a section belongs to. 'all' is the master override.
  const showFor = (buckets) =>
    viewMode === 'all' || (Array.isArray(buckets) && buckets.includes(viewMode));

  // Quick Start Meeting - Creates a meeting and navigates to start recording immediately
  const handleQuickStartMeeting = async () => {
    try {
      setStartingQuickMeeting(true);
      const startTime = new Date().toISOString();
      
      // Create a quick meeting with minimal info
      const response = await axios.post(`${API}/meeting-intelligence/meetings?token=${token}`, {
        title: `Quick Meeting - ${new Date().toLocaleString()}`,
        description: 'Quick start meeting - details to be filled after recording',
        meeting_type: 'physical',
        scheduled_start: startTime,
        is_quick_start: true
      });
      
      const meetingId = response.data.meeting?.id;
      
      if (meetingId) {
        toast.success('Meeting created! Starting recording...');
        // Navigate to the meeting with auto-start recording flag
        navigate(`/meeting-intelligence/meetings/${meetingId}?autoRecord=true`);
      } else {
        toast.error('Failed to create meeting');
      }
    } catch (error) {
      console.error('Failed to start quick meeting:', error);
      toast.error(error.response?.data?.detail || 'Failed to start quick meeting');
    } finally {
      setStartingQuickMeeting(false);
    }
  };

  useEffect(() => {
    if (token && !dataLoaded) {
      fetchAllData();
    }
  }, [token, dataLoaded]);

  // Progressive fetch: kick off every breakdown in parallel and flip the
  // page-level spinner off on the FIRST one that returns, not the last.
  // Each helper already sets its own state on success, so a slow fetch
  // (e.g. activity timeline aggregation) no longer freezes the rest of
  // the dashboard. A 3-second safety cap guarantees the page renders
  // even if every endpoint stalls.
  const fetchAllData = () => {
    setLoading(true);
    const tasks = [
      fetchStats(),
      fetchLeadStatusBreakdown(),
      fetchLifecycleBreakdown(),
      fetchSourceBreakdown(),
      fetchOwnerBreakdown(),
      fetchActivityTimeline(),
      fetchMarketingBreakdown(),
      fetchContactInfoBreakdown(),
    ].map((p) => Promise.resolve(p).catch((e) => {
      console.error('[Dashboard] tile fetch failed:', e);
      return null;
    }));

    let firstFulfilled = false;
    const markRendered = () => {
      if (firstFulfilled) return;
      firstFulfilled = true;
      setLoading(false);
    };
    const safetyTimer = setTimeout(markRendered, 3000);
    Promise.race(tasks).then(markRendered);
    Promise.allSettled(tasks).finally(() => {
      clearTimeout(safetyTimer);
      setDataLoaded(true);
      setLastFetchedAt(new Date());
    });
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats', error);
    }
  };

  const fetchLeadStatusBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/lead-status-breakdown?token=${token}`);
      setLeadStatusBreakdown(response.data.breakdown || []);
    } catch (error) {
      console.error('Failed to fetch lead status breakdown', error);
    }
  };

  const fetchLifecycleBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/lifecycle-breakdown?token=${token}`);
      setLifecycleBreakdown(response.data.breakdown || []);
    } catch (error) {
      console.error('Failed to fetch lifecycle breakdown', error);
    }
  };

  const fetchSourceBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/source-breakdown?token=${token}`);
      setSourceBreakdown(response.data.breakdown || []);
    } catch (error) {
      console.error('Failed to fetch source breakdown', error);
    }
  };

  const fetchOwnerBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/owner-breakdown?token=${token}`);
      setOwnerBreakdown(response.data.breakdown || []);
    } catch (error) {
      console.error('Failed to fetch owner breakdown', error);
    }
  };

  const fetchActivityTimeline = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/activity-timeline?token=${token}&days=30`);
      setActivityTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Failed to fetch activity timeline', error);
    }
  };

  const fetchMarketingBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/marketing-breakdown?token=${token}`);
      setMarketingBreakdown(response.data.breakdown || []);
    } catch (error) {
      console.error('Failed to fetch marketing breakdown', error);
    }
  };

  const fetchContactInfoBreakdown = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/contact-info-breakdown?token=${token}`);
      setContactInfoBreakdown(response.data);
    } catch (error) {
      console.error('Failed to fetch contact info breakdown', error);
    }
  };

  const syncHubSpotDates = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/integrations/hubspot/sync-dates?token=${token}`);
      await fetchAllData();
    } catch (error) {
      console.error('Failed to sync HubSpot dates', error);
    } finally {
      setSyncing(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, onClick }) => (
    <Card 
      className="card-premium hover:shadow-hover transition-all duration-300 cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1 min-w-0">
            <p className="text-sm text-muted-foreground uppercase tracking-wider font-medium truncate">{title}</p>
            <p className="text-3xl font-bold font-data tracking-tight">{value?.toLocaleString() || '0'}</p>
            {subtitle && <p className="text-sm text-muted-foreground truncate">{subtitle}</p>}
            {trend && (
              <div className={`flex items-center gap-1 text-sm font-medium ${trend === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {trendValue}
              </div>
            )}
          </div>
          <div className="p-3 rounded-xl bg-accent/10 group-hover:bg-accent/20 transition-colors flex-shrink-0 ml-2">
            <Icon className="h-6 w-6 text-accent" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Show loading state only when actively fetching
  const isLoading = loading && !dataLoaded;

  // Marketing context override — when the user's role OR email
  // contains "marketing", show the full Marketing Dashboard
  // (Awareness / Performance / Funnel / Campaigns / Sources / AI
  // Insights). The standard AgentOverviewDashboard shows
  // sales-centric KPIs (active deals, follow-ups, site visits) that
  // don't match what a marketing user needs: campaign performance,
  // CPL / ROAS, source attribution, UTM tracking.
  const _role = (user?.role || '').toLowerCase();
  const _email = (user?.email || '').toLowerCase();
  const isMarketingUser = _role.includes('marketing') || _email.includes('marketing');
  if (isMarketingUser) {
    return (
      <div className="p-6">
        <MarketingDashboardV2 embedded={true} />
      </div>
    );
  }

  // Agents get their own performance dashboard instead of the
  // company-wide contact analytics. (Placed after all hooks so the
  // Rules of Hooks are respected.)
  if (isAgent) {
    return <AgentOverviewDashboard />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-6 animate-fade-in">
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-80 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Welcome banner — friendly greeting strip that sits ABOVE the
          page header. Uses the auth user's display name; falls back to
          the email local-part, then the role, so a partially-filled
          profile still produces a readable line. */}
      {(() => {
        const display =
          user?.full_name ||
          (user?.email && user.email.split('@')[0]) ||
          (user?.role && user.role.toUpperCase()) ||
          'there';
        // Trim to the first name token for a more personal feel
        // ("Kalaivani Pandian" → "Kalaivani"; "ceo@test.com" → "CEO").
        const firstName = String(display).split(/[\s.]/)[0];
        return (
          <div className="flex items-center gap-3 px-1" data-testid="welcome-banner">
            <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
              <Users size={16} className="text-slate-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-800">
              Welcome back,{' '}
              <span className="text-slate-900">
                {firstName.toLowerCase() === firstName
                  ? firstName.charAt(0).toUpperCase() + firstName.slice(1)
                  : firstName}!
              </span>
            </h2>
          </div>
        );
      })()}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">Contact analytics and insights</p>
          {lastFetchedAt && (
            <p className="text-xs text-muted-foreground/80 mt-1">
              Data as of {lastFetchedAt.toLocaleString(undefined, {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Department-view selector. Defaults to "Strategic" which
              hides operational sections; switch to Sales / Marketing /
              Operations to reveal the matching department's widgets,
              or "All" to surface everything. Visible to anyone landing
              on /dashboard — CEO / admin / leadership all see the same
              control. */}
          <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">View</span>
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger className="w-[200px] gap-2" data-testid="dashboard-view-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="strategic">
                <span className="inline-flex items-center gap-2">
                  <LayoutDashboard size={13} /> Strategic Overview
                </span>
              </SelectItem>
              <SelectItem value="sales">
                <span className="inline-flex items-center gap-2">
                  <Users size={13} /> Sales View
                </span>
              </SelectItem>
              <SelectItem value="marketing">
                <span className="inline-flex items-center gap-2">
                  <MegaphoneIcon size={13} /> Marketing View
                </span>
              </SelectItem>
              <SelectItem value="operations">
                <span className="inline-flex items-center gap-2">
                  <Settings2 size={13} /> Operations View
                </span>
              </SelectItem>
              <SelectItem value="all">
                <span className="inline-flex items-center gap-2">
                  <BarChart3 size={13} /> All Departments
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          {/* Sync HubSpot — admin operation, only shown under
              Operations / All view modes so a CEO in Strategic view
              isn't tempted to push the wrong button. */}
          {showFor(['operations']) && (
            <Button
              variant="outline"
              onClick={syncHubSpotDates}
              disabled={syncing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync HubSpot'}
            </Button>
          )}
        </div>
      </div>

      {/* Quick Start Meeting Card — operational tool (agents start
          meeting recordings here). Hidden in Strategic view. */}
      {showFor(['operations']) && (
      <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white border-0 shadow-lg">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                <Mic className="h-8 w-8 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Quick Start Meeting</h3>
                <p className="text-red-100 text-sm mt-1">
                  Start recording immediately. Fill meeting details after you're done.
                </p>
              </div>
            </div>
            <Button 
              size="lg"
              className="bg-white text-red-600 hover:bg-red-50 font-semibold shadow-md"
              onClick={handleQuickStartMeeting}
              disabled={startingQuickMeeting}
            >
              {startingQuickMeeting ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Start Recording Now
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Executive Summary — eight strategic CEO summary cards +
          Quick Alerts + Financial Snapshot + Customer Insights, per
          the CEO-overview spec. Sits directly under the Quick Start
          Meeting card so a CEO/admin opening /dashboard reads the
          strategic scorecard first, then the windowed Revenue
          Performance panel beneath it. Soft-fails to skeletons when
          the caller isn't a leader role. */}
      <CEOExecutiveSummary />

      {/* Revenue Performance — windowed financials, pipeline health,
          monthly trend, top projects, ROAS. Pairs with the
          Executive Summary above. */}
      <RevenueWidget />

      {/* Stats Cards + Quick Stats — data-hygiene metrics (contact
          coverage, sales-team size). Operational; hidden in Strategic
          view. Wrapped in a fragment because the conditional contains
          two sibling <div> grids (the 4 stat cards + the 4 quick
          coloured chips below). */}
      {showFor(['operations']) && (
      <>
      <SectionBanner
        tone="slate"
        icon={Settings2}
        title="Operations · Contact Hygiene"
        subtitle="Coverage of phone numbers + sales-team headcount"
      />
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Total Contacts"
          value={stats?.total_contacts}
          subtitle={`${stats?.contacts_this_month || 0} this month`}
          icon={Users}
          onClick={() => navigate('/contacts')}
        />
        <StatCard
          title="Leads with Contact Info"
          value={stats?.leads_with_contact}
          subtitle={`${((stats?.leads_with_contact / stats?.total_contacts) * 100 || 0).toFixed(1)}% of total`}
          icon={Phone}
          onClick={() => navigate('/contacts')}
        />
        <StatCard
          title="Marketing (No Contact)"
          value={stats?.marketing_leads_no_contact}
          subtitle="Without phone/mobile"
          icon={PhoneOff}
        />
        <StatCard
          title="Sales Team Users"
          value={stats?.sales_users}
          subtitle="Sales Agent, Director, Telesales"
          icon={UserCircle}
        />
      </div>

      {/* Quick Stats Row - Split by Contact Info */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4 bg-emerald-50 border-emerald-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-700 font-medium">Today (With Contact)</p>
              <p className="text-2xl font-bold font-data text-emerald-800">{stats?.contacts_today_with_contact || 0}</p>
            </div>
            <Phone className="h-5 w-5 text-emerald-600" />
          </div>
        </Card>
        <Card className="p-4 bg-sky-50 border-sky-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-sky-700 font-medium">This Week (With Contact)</p>
              <p className="text-2xl font-bold font-data text-sky-800">{stats?.contacts_this_week_with_contact || 0}</p>
            </div>
            <Phone className="h-5 w-5 text-sky-600" />
          </div>
        </Card>
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-amber-700 font-medium">This Month (With Contact)</p>
              <p className="text-2xl font-bold font-data text-amber-800">{stats?.contacts_this_month_with_contact || 0}</p>
            </div>
            <Phone className="h-5 w-5 text-amber-600" />
          </div>
        </Card>
        <Card className="p-4 bg-violet-50 border-violet-200">
          <p className="text-sm text-violet-700 font-medium">Total Deals</p>
          <p className="text-2xl font-bold font-data text-violet-800">{stats?.total_deals || 0}</p>
        </Card>
      </div>
      </>
      )}

      {/* Leadership Overview block — Marketing Leads overview, Top
          Performers, New Inquiries, Today's Schedule. The inner Agent
          Talktime panel is operational (per-agent call counts) so we
          only surface it under Sales / Operations / All views. */}
      <LeadershipOverviewBlock
        showAgentTalktime={showFor(['sales', 'operations'])}
      />

      {/* Charts Row 1 — Lead Status Distribution + Lifecycle Stage
          Distribution. Sales-operational; the Executive Summary
          Status Rail covers the strategic version. */}
      {showFor(['sales']) && (
      <>
      <SectionBanner
        tone="blue"
        icon={Users}
        title="Sales · Funnel Distribution"
        subtitle="Lead status pie + lifecycle-stage bar"
      />
      <div className="grid grid-cols-2 gap-6">
        {/* Lead Status Breakdown */}
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <PieChartIcon className="h-5 w-5 text-accent" />
              Lead Status Distribution
              <HelpInfo
                align="inline"
                title="How to read Lead Status Distribution"
                steps={[
                  'A pie of all contacts grouped by their lead status — New Lead, attempts made, Connected & Interested, Not Interested, and so on.',
                  'Each slice is a status; the % label is that status’s share of total contacts.',
                  'A big "No status" or early-attempt slice means leads are piling up un-worked — those are the ones to chase.',
                ]}
                tip="Healthy pipelines have most contacts in mid-funnel statuses, not stuck at first contact."
              />
            </CardTitle>
            <CardDescription>Contacts by lead status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <PieChart>
                  <Pie
                    data={leadStatusBreakdown.slice(0, 8)}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {leadStatusBreakdown.slice(0, 8).map((entry, index) => (
                      <Cell 
                        key={entry.status} 
                        fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                      />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name) => [value.toLocaleString(), name]}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Lifecycle Stage Breakdown */}
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Zap className="h-5 w-5 text-accent" />
              Lifecycle Stage Distribution
              <HelpInfo
                align="inline"
                title="How to read Lifecycle Stage Distribution"
                steps={[
                  'Contacts grouped by lifecycle stage — Cold, Under Qualification, Pending Qualification, Sales Qualified, and so on.',
                  'Each bar’s length is how many contacts sit at that stage right now.',
                  'Lifecycle stage is the high-level funnel position; lead status is the day-to-day working state — read them together.',
                ]}
                tip="A large Cold bar with a thin Sales Qualified bar means qualification is the bottleneck."
              />
            </CardTitle>
            <CardDescription>Contacts by lifecycle stage</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={lifecycleBreakdown.slice(0, 10)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis 
                    dataKey="stage" 
                    type="category" 
                    width={100}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'Contacts']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                  />
                  <Bar dataKey="count" fill="#F97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Charts Row 2 — Lead Source (Marketing) + Contacts by Owner
          (Sales / HOD). Hidden unless those department views are
          selected. */}
      {showFor(['sales', 'marketing']) && (
      <>
      <SectionBanner
        tone="violet"
        icon={MegaphoneIcon}
        title="Sales & Marketing · Source Mix + Ownership"
        subtitle="Where leads come from + which agent owns them"
      />
      <div className="grid grid-cols-2 gap-6">
        {/* Lead Source Breakdown */}
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Globe className="h-5 w-5 text-accent" />
              Lead Source Distribution
              <HelpInfo
                align="inline"
                title="How to read Lead Source Distribution"
                steps={[
                  'A bar per lead source — portals (Bayut, Property Finder), ad platforms, website, referrals, walk-ins.',
                  'Bar height is how many contacts came from that source.',
                  'Pair this with which sources actually convert — high volume from a source that never qualifies is cheap, not valuable.',
                ]}
                tip="The tallest bar is your biggest lead engine — know its cost and conversion before leaning on it."
              />
            </CardTitle>
            <CardDescription>Where your contacts come from</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={sourceBreakdown.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis 
                    dataKey="source" 
                    angle={-45}
                    textAnchor="end"
                    height={70}
                    tick={{ fontSize: 10 }}
                    interval={0}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'Contacts']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                  />
                  <Bar dataKey="count" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Contact Owner Breakdown */}
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <UserCircle className="h-5 w-5 text-accent" />
              Contacts by Owner
              <HelpInfo
                align="inline"
                title="How to read Contacts by Owner"
                steps={[
                  'One row per team member, showing how many contacts are assigned to them.',
                  'The progress bar is that owner’s share of the total contact base.',
                  'An "Unassigned" row with a big count means leads are sitting with no owner — assign them so they get worked.',
                ]}
                tip="Wildly uneven owner counts are worth rebalancing — an overloaded agent drops leads."
              />
            </CardTitle>
            <CardDescription>Distribution across team members</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {ownerBreakdown.slice(0, 10).map((owner, index) => (
                <div key={owner.owner_id || 'unassigned'} className="flex items-center gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm flex-shrink-0"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  >
                    {owner.owner_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{owner.owner_name}</p>
                    <div className="flex items-center gap-2">
                      <Progress value={owner.percentage} className="h-2 flex-1" />
                      <span className="text-xs text-muted-foreground w-12 text-right">
                        {owner.count.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Activity Timeline — 30-day contact-creation line chart.
          Marketing-operational; the Revenue Trend (6-month) on the
          Revenue Performance panel is the strategic version. */}
      {showFor(['marketing']) && (
      <>
      <SectionBanner
        tone="amber"
        icon={Calendar}
        title="Marketing · Activity Timeline"
        subtitle="30-day inflow of new contacts (compare with 6-month revenue trend above)"
      />
      <Card className="card-premium">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 flex-wrap">
            <Calendar className="h-5 w-5 text-accent" />
            Contact Activity (Last 30 Days)
            <HelpInfo
              align="inline"
              title="How to read Contact Activity"
              steps={[
                'A line of how many new contacts were created each day over the last 30 days.',
                'Peaks usually line up with campaign launches or portal pushes; flat stretches mean lead generation went quiet.',
                'Hover any point to see the exact date and count.',
              ]}
              tip="A steady downward trend in new contacts is an early warning — the top of the funnel is drying up."
            />
          </CardTitle>
          <CardDescription>New contacts created over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <LineChart data={activityTimeline}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip 
                  formatter={(value) => [value.toLocaleString(), 'Contacts']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', year: 'numeric' })}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#F97316" 
                  strokeWidth={2}
                  dot={{ fill: '#F97316', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {/* Contact Info Split Charts — data-hygiene (which contacts
          have phone, which have email). Operational; hidden in
          Strategic / Sales / Marketing views. */}
      {showFor(['operations']) && (
      <>
      <SectionBanner
        tone="emerald"
        icon={Settings2}
        title="Operations · Contact Reachability"
        subtitle="Leads with vs without phone numbers, broken down by status"
      />
      <div className="grid grid-cols-2 gap-6">
        {/* Contact Info Distribution - Primary Focus */}
        <Card className="card-premium border-2 border-emerald-200">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              <Phone className="h-5 w-5 text-emerald-600" />
              Contact Info Distribution
              <HelpInfo
                align="inline"
                title="How to read Contact Info Distribution"
                steps={[
                  'Splits the contact base into leads that HAVE a phone/mobile vs those that don’t.',
                  'Leads without contact info can’t be called — they’re effectively unworkable until enriched.',
                  'A large "without" slice points to a data-quality gap in how leads are captured.',
                ]}
                tip="Chase the source of contactless leads — a portal or form that isn’t capturing phone numbers is wasting spend."
              />
            </CardTitle>
            <CardDescription>Leads with vs without phone/mobile - <span className="text-emerald-600 font-semibold">Primary Focus</span></CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'With Contact Info', value: contactInfoBreakdown?.summary?.with_contact || 0 },
                      { name: 'Without Contact Info', value: contactInfoBreakdown?.summary?.without_contact || 0 }
                    ]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#94A3B8" />
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'Leads']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-700">{contactInfoBreakdown?.summary?.with_contact?.toLocaleString() || 0}</p>
                <p className="text-xs text-emerald-600">With Contact Info</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-lg text-center">
                <p className="text-2xl font-bold text-slate-600">{contactInfoBreakdown?.summary?.without_contact?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-500">Without Contact Info</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Status by Contact Info */}
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              Lead Status by Contact Info
              <HelpInfo
                align="inline"
                title="How to read this panel"
                steps={[
                  'The top lead statuses, split into two lists: leads that have contact info and leads that don’t.',
                  'It shows where your contactable vs non-contactable leads are sitting in the funnel.',
                  'Statuses high in the "Without Contact Info" list are stuck — they can’t progress without a phone number.',
                ]}
                tip={'If qualified-looking statuses appear under “Without Contact Info”, enrich those first — they’re close but unreachable.'}
              />
            </CardTitle>
            <CardDescription>Top statuses split by contact availability</CardDescription>
          </CardHeader>
          <CardContent>
            {/* One row per lead status, with the With / Without Contact
                counts side by side and a Total — far easier to scan
                than two separate scrolling lists. */}
            {(() => {
              const withList = contactInfoBreakdown?.status_with_contact || [];
              const withoutList = contactInfoBreakdown?.status_without_contact || [];
              const byStatus = {};
              withList.forEach((s) => {
                byStatus[s.status] = { status: s.status, withC: s.count || 0, withoutC: 0 };
              });
              withoutList.forEach((s) => {
                if (!byStatus[s.status]) byStatus[s.status] = { status: s.status, withC: 0, withoutC: 0 };
                byStatus[s.status].withoutC = s.count || 0;
              });
              const rows = Object.values(byStatus)
                .map((r) => ({ ...r, total: r.withC + r.withoutC }))
                .sort((a, b) => b.total - a.total);
              const sumWith = rows.reduce((n, r) => n + r.withC, 0);
              const sumWithout = rows.reduce((n, r) => n + r.withoutC, 0);

              if (rows.length === 0) {
                return (
                  <p className="text-sm text-slate-400 text-center py-8">
                    No lead-status data available.
                  </p>
                );
              }

              return (
                <div className="overflow-x-auto max-h-80 overflow-y-auto rounded-lg border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                        <th className="text-left py-2.5 px-3 font-semibold">Lead Status</th>
                        <th className="text-center py-2.5 px-3 font-semibold">
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <Phone className="h-3 w-3" /> With Contact
                          </span>
                        </th>
                        <th className="text-center py-2.5 px-3 font-semibold">
                          <span className="inline-flex items-center gap-1 text-slate-400">
                            <PhoneOff className="h-3 w-3" /> Without
                          </span>
                        </th>
                        <th className="text-right py-2.5 px-3 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.status} className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors">
                          <td className="py-2 px-3 font-medium text-slate-800 capitalize">
                            {r.status}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {r.withC > 0 ? (
                              <span className="inline-block min-w-[2.25rem] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                                {r.withC}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-center">
                            {r.withoutC > 0 ? (
                              <span className="inline-block min-w-[2.25rem] px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 text-xs font-semibold">
                                {r.withoutC}
                              </span>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right font-bold text-slate-900">
                            {r.total}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="sticky bottom-0 bg-slate-50">
                      <tr className="border-t-2 border-slate-200 text-sm font-semibold">
                        <td className="py-2.5 px-3 text-slate-700">Total</td>
                        <td className="py-2.5 px-3 text-center text-emerald-700">{sumWith}</td>
                        <td className="py-2.5 px-3 text-center text-slate-600">{sumWithout}</td>
                        <td className="py-2.5 px-3 text-right text-slate-900">{sumWith + sumWithout}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Original Marketing Status — Marketing-operational
          (campaign-eligibility split). Hidden in Strategic / Sales /
          Operations views. */}
      {showFor(['marketing']) && (
      <>
      <SectionBanner
        tone="rose"
        icon={MegaphoneIcon}
        title="Marketing · Campaign Eligibility"
        subtitle="Marketing vs non-marketing contacts (who can be campaigned to)"
      />
      <div className="grid grid-cols-2 gap-6">
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              Marketing Status
              <HelpInfo
                align="inline"
                title="How to read Marketing Status"
                steps={[
                  'A donut splitting contacts into Marketing vs Non-Marketing — whether they’re eligible for marketing campaigns.',
                  'It shows how much of the base you can legitimately run email / WhatsApp / ad campaigns to.',
                  'A small Marketing slice limits campaign reach — growing opt-ins is a lead-nurture lever.',
                ]}
                tip="Non-marketing contacts still need 1:1 follow-up — they’re just off the bulk-campaign list."
              />
            </CardTitle>
            <CardDescription>Marketing vs Non-Marketing contacts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-48 w-full">
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <PieChart>
                  <Pie
                    data={marketingBreakdown}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#94A3B8" />
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [value.toLocaleString(), 'Contacts']}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #E7E5E4' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Lead Statuses Table */}
        <Card className="card-premium">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 flex-wrap">
              Top Lead Statuses
              <HelpInfo
                align="inline"
                title="How to read Top Lead Statuses"
                steps={[
                  'A ranked list of the most common lead statuses with their contact counts.',
                  'It’s the Lead Status pie as a sorted table — easier to read exact numbers.',
                  'The top entry is where most of your contacts currently sit.',
                ]}
                tip="If an early status like “First Attempt - No Response” tops the list, the team is generating leads faster than it’s working them."
              />
            </CardTitle>
            <CardDescription>Most common lead statuses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {leadStatusBreakdown.slice(0, 8).map((status, index) => (
                <div key={status.status} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: STATUS_COLORS[status.status] || COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm truncate">{status.status}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Badge variant="secondary" className="font-data text-xs">
                      {status.count.toLocaleString()}
                    </Badge>
                    <span className="text-xs text-muted-foreground w-12 text-right">
                      {status.percentage}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Friendly "you're in Strategic view" hint at the bottom so a
          CEO landing here knows operational analytics exist if they
          want them — just not on by default. Hidden when any other
          view is active so it doesn't compete with the data. */}
      {viewMode === 'strategic' && (
        <div className="text-center py-4 text-xs text-slate-400 border-t border-slate-100">
          Operational analytics (contact hygiene, distribution charts, activity timeline)
          are hidden in <span className="font-medium text-slate-500">Strategic Overview</span>.
          Switch the department selector above to surface them.
        </div>
      )}
    </div>
  );
};

export default Dashboard;
